// Dashboard'daki abteilung kartına tıklayınca: o abteilung'ın tüm offen Aufträge
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api } from '../lib/api';
import { StandCell } from '../components/StandCell';
import { EmpfohleneReihenfolge } from '../components/EmpfohleneReihenfolge';

const MODULE_LABELS: Record<string, string> = {
  drueckteile: 'Drückteile', laufraeder: 'Laufräder',
  baugruppen: 'Baugruppen', montage: 'Montage',
};

export function AbteilungDetail() {
  const { id } = useParams<{ id: string }>();
  const abtId = parseInt(id ?? '0');
  const list = useApi(() => api.sessions.abteilungAuftraege(abtId), [abtId]);
  const standWerte = useApi(() => api.lookup.standWerte(), []);

  if (list.loading) return <div className="p-8 text-slate-500">Lädt …</div>;
  if (!list.data) return <div className="p-8 text-red-600">Fehler</div>;
  const { data, abteilung } = list.data;

  return (
    <div className="p-8">
      <Link to="/dashboard" className="text-sm text-brand hover:underline">← Zurück zum Dashboard</Link>
      <header className="mt-4 mb-6">
        <div className="text-xs uppercase text-slate-500">Abteilung</div>
        <h1 className="text-2xl font-bold flex items-center gap-3" style={{ color: darken(abteilung.farbe) }}>
          <span className="w-3 h-3 rounded-full" style={{ background: abteilung.farbe }} />
          {abteilung.name}
        </h1>
        <div className="text-sm text-slate-500 mt-1">{data.length} offene Aufträge</div>
      </header>

      {/* A3 — Empfohlene Reihenfolge */}
      <div className="mb-6">
        <EmpfohleneReihenfolge
          abteilungId={abtId}
          abteilungName={abteilung.name}
          limit={5}
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Modul</th>
              <th className="text-left px-4 py-2 font-medium">Bestellnr</th>
              <th className="text-left px-4 py-2 font-medium">DV-Nr</th>
              <th className="text-left px-4 py-2 font-medium">Kunde</th>
              <th className="text-left px-4 py-2 font-medium">Bezeichnung</th>
              <th className="text-right px-4 py-2 font-medium">Stk</th>
              <th className="text-left px-4 py-2 font-medium">Liefertermin</th>
              <th className="text-center px-4 py-2 font-medium">KW</th>
              <th className="text-right px-4 py-2 font-medium">Zeit</th>
              <th className="text-left px-4 py-2 font-medium">Stand</th>
              <th className="text-left px-4 py-2 font-medium">SB</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.length === 0 && (
              <tr><td colSpan={11} className="text-center py-12 text-slate-500">Keine offenen Aufträge in dieser Abteilung</td></tr>
            )}
            {data.map(r => (
              <tr key={`${r.modul}:${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-2">
                  <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                    {MODULE_LABELS[r.modul]}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-xs">{r.bestellnr}</td>
                <td className="px-4 py-2 font-mono text-xs">
                  <Link to={`/${r.modul}/${r.id}`} className="text-brand hover:underline">
                    {r.dv_nummer ?? '—'}
                  </Link>
                </td>
                <td className="px-4 py-2">{r.kunde_name ?? '—'}</td>
                <td className="px-4 py-2 max-w-[280px] truncate" title={r.bezeichnung ?? ''}>
                  {r.bezeichnung ?? '—'}
                  {r.priority === 'eilig'    && <span className="ml-2 text-xs font-bold text-red-600">EILIG</span>}
                  {r.priority === 'kritisch' && <span className="ml-2 text-xs font-bold text-red-800">KRITISCH</span>}
                </td>
                <td className="px-4 py-2 text-right">{r.stueck}</td>
                <td className="px-4 py-2 whitespace-nowrap"><LieferDatum iso={r.lieferdatum} /></td>
                <td className="px-4 py-2 text-center">
                  <span className="bg-sky-100 text-sky-900 px-2 py-0.5 rounded text-xs font-semibold">{r.kw}</span>
                </td>
                <td className="px-4 py-2 text-right">{r.gesamtzeit}h</td>
                <td className="px-4 py-2 min-w-[160px]">
                  <StandCell
                    drueckteilId={r.id}
                    currentStandId={r.stand_id}
                    currentStandWert={r.stand_wert}
                    currentStandFarbe={r.stand_farbe}
                    updatedAt={r.updated_at}
                    standWerte={standWerte.data?.data ?? []}
                    onChanged={() => list.reload()}
                    modulePath={r.modul as 'drueckteile' | 'laufraeder' | 'baugruppen' | 'montage'} />
                </td>
                <td className="px-4 py-2">{r.sachbearbeiter_name ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LieferDatum({ iso }: { iso: string }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / 86400000);
  const formatted = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  let cls = 'text-slate-700';
  let badge: string | null = null;
  if (days < 0)       { cls = 'text-red-700 font-semibold';    badge = `${Math.abs(days)}T verspätet`; }
  else if (days <= 3) { cls = 'text-orange-700 font-semibold'; badge = days === 0 ? 'HEUTE' : days === 1 ? 'morgen' : `in ${days}T`; }
  else if (days <= 7) { cls = 'text-amber-700'; }
  return <span className={cls}>{formatted}{badge && <span className="ml-1 text-[10px] opacity-75">· {badge}</span>}</span>;
}

function darken(hex: string): string {
  if (!hex || !hex.startsWith('#')) return '#1E293B';
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.round(((n >> 16) & 0xFF) * 0.55));
  const g = Math.max(0, Math.round(((n >> 8)  & 0xFF) * 0.55));
  const b = Math.max(0, Math.round((n & 0xFF) * 0.55));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
